import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { LogOut, Download, FileText, Building2, Mail, Phone, MapPin, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import Varni2FlyLogo from '../components/Varni2FlyLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const B2BDashboard = () => {
  const navigate = useNavigate();
  const [agent, setAgent] = useState(null);
  const [ratecards, setRatecards] = useState([]);
  const [myInquiries, setMyInquiries] = useState([]);
  const token = localStorage.getItem('b2b_token');

  const logout = useCallback(() => {
    localStorage.removeItem('b2b_token');
    localStorage.removeItem('b2b_agent');
    navigate('/b2b/login');
  }, [navigate]);

  useEffect(() => {
    if (!token) {
      navigate('/b2b/login');
      return;
    }
    const cfg = { headers: { Authorization: `Bearer ${token}` } };
    const load = async () => {
      try {
        const [me, rc, inq] = await Promise.all([
          axios.get(`${API}/b2b/me`, cfg),
          axios.get(`${API}/b2b/ratecards`, cfg),
          axios.get(`${API}/b2b/inquiries`, cfg)
        ]);
        setAgent(me.data);
        setRatecards(rc.data);
        setMyInquiries(inq.data);
      } catch (err) {
        if (err.response?.status === 401 || err.response?.status === 403) {
          toast.error(err.response?.data?.detail || 'Session expired');
          logout();
        }
      }
    };
    load();
  }, [token, navigate, logout]);

  const download = async (rc) => {
    try {
      const res = await axios.get(`${API}/b2b/ratecards/${rc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', rc.filename || `${rc.title}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded: ${rc.title}`);
    } catch (err) {
      toast.error('Download failed. Please retry.');
    }
  };

  if (!agent) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Varni2FlyLogo size="sm" />
            <span className="hidden md:inline text-gray-300">|</span>
            <span className="hidden md:inline text-sm text-gray-500">B2B Partner Portal</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden md:block text-right">
              <div className="text-sm font-bold text-gray-900">{agent.agency_name}</div>
              <div className="text-xs text-gray-500">{agent.name}</div>
            </div>
            <Button variant="outline" size="sm" onClick={logout} data-testid="b2b-logout">
              <LogOut size={14} className="mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Welcome banner */}
        <Card className="mb-8 border-l-4 border-l-[#2D8DB4] overflow-hidden">
          <CardContent className="p-6 bg-gradient-to-r from-[#2D8DB4]/5 to-[#F7945D]/5">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <p className="text-[#F7945D] uppercase font-semibold tracking-wider text-xs mb-1">Welcome back</p>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{agent.name}</h1>
                <p className="text-gray-600 text-sm mt-1">Manage your B2B bookings and download net rate cards below</p>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#2D8DB4]">{ratecards.length}</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">Rate Cards</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#F7945D]">{myInquiries.length}</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">Inquiries</div>
                </div>
                <div className="text-center">
                  <Badge className="bg-green-600 hover:bg-green-700">{agent.status}</Badge>
                  <div className="text-xs text-gray-500 uppercase tracking-wider mt-1">Status</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="ratecards">
          <TabsList className="mb-6">
            <TabsTrigger value="ratecards" data-testid="b2b-tab-ratecards">Rate Cards & Itineraries</TabsTrigger>
            <TabsTrigger value="inquiries" data-testid="b2b-tab-inquiries">My Inquiries</TabsTrigger>
            <TabsTrigger value="profile" data-testid="b2b-tab-profile">Agency Profile</TabsTrigger>
          </TabsList>

          <TabsContent value="ratecards">
            <div className="mb-4">
              <h2 className="text-xl font-bold">Net Rate Cards & White-Label Itineraries</h2>
              <p className="text-sm text-gray-500">Confidential B2B pricing. Mark up at your discretion.</p>
            </div>
            {ratecards.length === 0 ? (
              <Card><CardContent className="p-12 text-center text-gray-500">No rate cards uploaded yet.</CardContent></Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {ratecards.map((rc) => (
                  <Card key={rc.id} className="hover:shadow-lg transition-shadow" data-testid={`ratecard-${rc.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-14 bg-gradient-to-br from-[#2D8DB4] to-[#F7945D] rounded flex items-center justify-center flex-shrink-0">
                          <FileText className="text-white" size={22} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h3 className="font-bold text-gray-900 leading-tight">{rc.title}</h3>
                            <Badge variant="secondary" className="text-xs flex-shrink-0">{rc.category}</Badge>
                          </div>
                          <p className="text-sm text-gray-600 mb-3 line-clamp-2">{rc.description}</p>
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs text-gray-500">{rc.filename} · {fmtSize(rc.file_size)}</span>
                            <Button size="sm" onClick={() => download(rc)} data-testid={`download-${rc.id}`} className="bg-[#2D8DB4] hover:bg-[#247a9a]">
                              <Download size={14} className="mr-1" /> Download
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="inquiries">
            <h2 className="text-xl font-bold mb-4">Your Inquiries with Varni Visa</h2>
            {myInquiries.length === 0 ? (
              <Card><CardContent className="p-12 text-center text-gray-500">
                No inquiries yet. <Link to="/contact" className="text-[#2D8DB4] underline">Submit one</Link>.
              </CardContent></Card>
            ) : (
              <div className="space-y-3">
                {myInquiries.map((inq) => (
                  <Card key={inq.id} className="border-l-4 border-l-[#2D8DB4]">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start gap-3">
                        <div>
                          <h4 className="font-bold">{inq.subject || inq.inquiry_type}</h4>
                          <p className="text-sm text-gray-600 mt-1">{inq.message}</p>
                        </div>
                        <Badge>{inq.status}</Badge>
                      </div>
                      <div className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                        <Calendar size={12} /> {new Date(inq.created_at).toLocaleString()}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle>Agency Profile</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-8 text-sm">
                  <div>
                    <dt className="text-xs text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1"><Building2 size={12} /> Agency Name</dt>
                    <dd className="font-semibold">{agent.agency_name}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500 uppercase tracking-wider mb-1">Contact Person</dt>
                    <dd className="font-semibold">{agent.name}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1"><Mail size={12} /> Email</dt>
                    <dd className="font-semibold">{agent.email}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1"><Phone size={12} /> Phone</dt>
                    <dd className="font-semibold">{agent.phone}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1"><MapPin size={12} /> City</dt>
                    <dd className="font-semibold">{agent.city || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500 uppercase tracking-wider mb-1">GST / IATA</dt>
                    <dd className="font-semibold">{agent.gst_iata || '—'}</dd>
                  </div>
                </dl>
                <p className="text-xs text-gray-400 mt-6">
                  To update agency details, contact us at <a href="mailto:info@varnivisa.com" className="text-[#2D8DB4]">info@varnivisa.com</a>.
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default B2BDashboard;
